/**
 * AGENT RUNNER - PREMIUM EDITION
 * 
 * The immersive experience of watching your AI agent work.
 * Design Philosophy: Mission control meets progress visualization
 * 
 * Features:
 * - Dramatic step-by-step progress with animations
 * - Real-time stats (time elapsed, credits)
 * - Celebration effects on completion
 * - Rich result previews
 * - Abort capability
 */

import React, { useState, useEffect, useRef } from 'react';

// Agent-specific configurations
const AGENT_CONFIGS = {
  content_engine: {
    name: 'Hermes',
    emoji: '👟',
    gradient: 'from-orange-600 via-amber-500 to-yellow-500',
    gradientBg: 'from-orange-500/10 to-amber-500/10',
    glow: 'shadow-orange-500/30',
    runningEmoji: '🔥',
    credits: 35,
    estimatedTime: 180, // seconds
    description: 'Divine content forged in celestial fire',
    steps: [
      { emoji: '🔥', name: 'Igniting The Forge', description: 'Heating up the creative furnace' },
      { emoji: '🔍', name: 'Scanning The Horizon', description: 'Finding trending topics across realms' },
      { emoji: '📜', name: 'Drafting The Blueprint', description: 'Creating strategic content brief' },
      { emoji: '⚒️', name: 'Forging The Content', description: 'Crafting SEO-optimized long-form content' },
      { emoji: '🎯', name: 'Tempering For SEO', description: 'Strengthening with keywords and structure' },
      { emoji: '⚔️', name: 'Sharpening Headlines', description: 'Testing multiple headline variations' },
      { emoji: '🪽', name: 'Crafting Swift Messages', description: 'Creating platform-specific content' },
      { emoji: '#️⃣', name: 'Enchanting Hashtags', description: 'Building viral hashtag strategy' },
      { emoji: '📧', name: 'Sealing The Scroll', description: 'Converting to email newsletter' },
      { emoji: '🎨', name: 'Illuminating Visuals', description: 'AI-powered image creation' },
      { emoji: '🔮', name: 'Divine Prophecy', description: 'Predicting performance with foresight' },
      { emoji: '📦', name: 'Completing The Arsenal', description: 'Finalizing your content package' },
    ],
    successTitle: 'Content Forged!',
    successEmoji: '🔥',
    successMessage: 'Hermes has completed your divine content package'
  },
  competitor_spy: {
    name: 'Athena',
    emoji: '🦉',
    gradient: 'from-amber-500 via-orange-500 to-red-500',
    gradientBg: 'from-amber-500/10 to-orange-500/10',
    glow: 'shadow-amber-500/30',
    runningEmoji: '🔍',
    credits: 15,
    estimatedTime: 120,
    description: 'Competitive intelligence analysis',
    steps: [
      { emoji: '🌐', name: 'Scanning Your Website', description: 'Analyzing your content and positioning' },
      { emoji: '🔍', name: 'Identifying Your Niche', description: 'Understanding your market space' },
      { emoji: '🎯', name: 'Searching Competitors', description: 'Finding relevant competitors' },
      { emoji: '🕷️', name: 'Crawling Competitor Sites', description: 'Deep analysis of competitor content' },
      { emoji: '📊', name: 'Analyzing Strategies', description: 'Comparing content approaches' },
      { emoji: '🔎', name: 'Finding Content Gaps', description: 'Identifying opportunities' },
      { emoji: '💡', name: 'Generating Insights', description: 'Creating actionable recommendations' },
      { emoji: '⚔️', name: 'Building Battle Strategy', description: 'Planning your content attack' },
      { emoji: '📋', name: 'Compiling Report', description: 'Finalizing intelligence report' },
    ],
    successTitle: 'Intelligence Report Ready!',
    successEmoji: '🎯',
    successMessage: 'Your competitive analysis is complete'
  },
  distribution_machine: {
    name: 'Distribution Machine',
    emoji: '🚀',
    gradient: 'from-emerald-500 via-cyan-500 to-teal-500',
    gradientBg: 'from-emerald-500/10 to-cyan-500/10',
    glow: 'shadow-emerald-500/30',
    runningEmoji: '📤',
    credits: 20,
    estimatedTime: 120,
    description: 'Multi-platform content distribution',
    steps: [
      { emoji: '📦', name: 'Loading Content', description: 'Preparing content for distribution' },
      { emoji: '✂️', name: 'Optimizing Content', description: 'Adapting for each platform' },
      { emoji: '💼', name: 'Preparing LinkedIn', description: 'Professional network optimization' },
      { emoji: '🐦', name: 'Preparing Twitter', description: 'Thread and engagement optimization' },
      { emoji: '📸', name: 'Preparing Instagram', description: 'Visual and caption optimization' },
      { emoji: '📘', name: 'Preparing Facebook', description: 'Social engagement optimization' },
      { emoji: '🎵', name: 'Preparing TikTok', description: 'Short-form video script' },
      { emoji: '📊', name: 'Scheduling Posts', description: 'Optimal timing analysis' },
      { emoji: '✅', name: 'Finalizing Distribution', description: 'Confirming all platforms' },
    ],
    successTitle: 'Content Distributed!',
    successEmoji: '🚀',
    successMessage: 'Your content is ready for all platforms'
  },
};

// Animated progress ring
const ProgressRing = ({ progress, size = 120, strokeWidth = 8, gradient }) => {
  const radius = (size - strokeWidth) / 2;
  const circumference = radius * 2 * Math.PI;
  const offset = circumference - (progress / 100) * circumference;
  
  return (
    <svg width={size} height={size} className="transform -rotate-90">
      <defs>
        <linearGradient id="progressGradient" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#8b5cf6" />
          <stop offset="50%" stopColor="#3b82f6" />
          <stop offset="100%" stopColor="#06b6d4" />
        </linearGradient>
      </defs>
      {/* Background circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="rgba(255,255,255,0.1)"
        strokeWidth={strokeWidth}
      />
      {/* Progress circle */}
      <circle
        cx={size / 2}
        cy={size / 2}
        r={radius}
        fill="none"
        stroke="url(#progressGradient)"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeDasharray={circumference}
        strokeDashoffset={offset}
        className="transition-all duration-500 ease-out"
      />
    </svg>
  );
};

// Pre-generate confetti positions (stable across renders)
const CONFETTI_COLORS = ['#8b5cf6', '#3b82f6', '#06b6d4', '#10b981', '#f59e0b', '#ef4444'];
const CONFETTI_ITEMS = Array.from({ length: 50 }, (_, i) => ({
  left: (i * 2 + (i % 7) * 11) % 100,
  color: CONFETTI_COLORS[i % 6],
  delay: (i * 0.04) % 2,
  duration: 2 + (i % 3),
  isCircle: i % 2 === 0
}));

// Celebration confetti
const Confetti = ({ show }) => {
  if (!show) return null;
  
  return (
    <div className="fixed inset-0 pointer-events-none z-50">
      {CONFETTI_ITEMS.map((item, i) => (
        <div
          key={i}
          className="absolute w-3 h-3 animate-confetti"
          style={{
            left: `${item.left}%`,
            top: '-20px',
            backgroundColor: item.color,
            animationDelay: `${item.delay}s`,
            animationDuration: `${item.duration}s`,
            borderRadius: item.isCircle ? '50%' : '0'
          }}
        />
      ))}
      <style>{`
        @keyframes confetti {
          0% { transform: translateY(0) rotate(0deg); opacity: 1; }
          100% { transform: translateY(100vh) rotate(720deg); opacity: 0; }
        }
        .animate-confetti { animation: confetti 3s ease-out forwards; }
      `}</style>
    </div>
  );
};

// Step progress item
const StepItem = ({ step, index, status, isLast }) => {
  const statusStyles = {
    complete: {
      ring: 'bg-gradient-to-br from-emerald-500 to-green-500 shadow-lg shadow-emerald-500/30',
      text: 'text-emerald-400',
      line: 'bg-emerald-500',
      bg: 'bg-emerald-500/10'
    },
    active: {
      ring: 'bg-gradient-to-br from-purple-500 to-blue-500 shadow-lg shadow-purple-500/30 animate-pulse',
      text: 'text-white',
      line: 'bg-gradient-to-b from-purple-500 to-gray-700',
      bg: 'bg-purple-500/10'
    },
    pending: {
      ring: 'bg-gray-800',
      text: 'text-gray-500',
      line: 'bg-gray-700',
      bg: ''
    }
  };
  
  const style = statusStyles[status];
  
  return (
    <div className="flex gap-4">
      {/* Timeline */}
      <div className="flex flex-col items-center">
        <div className={`relative w-10 h-10 rounded-full ${style.ring} flex items-center justify-center transition-all duration-500`}>
          {status === 'complete' ? (
            <span className="text-white font-bold">✓</span>
          ) : (
            <span className="text-lg">{step.emoji}</span>
          )}
          {status === 'active' && (
            <div className="absolute inset-0 rounded-full border-2 border-purple-400 animate-ping opacity-50" />
          )}
        </div>
        {!isLast && (
          <div className={`w-0.5 h-12 ${style.line} transition-colors duration-500`} />
        )}
      </div>
      
      {/* Content */}
      <div className={`flex-1 pb-4 ${style.bg} rounded-lg ${status !== 'pending' ? 'px-4 py-3' : ''} transition-all duration-300`}>
        <div className="flex items-center justify-between">
          <h4 className={`font-semibold ${style.text} transition-colors duration-300`}>
            {step.name}
          </h4>
          {status === 'active' && (
            <span className="text-purple-400 text-sm animate-pulse">Processing...</span>
          )}
          {status === 'complete' && (
            <span className="text-emerald-400 text-sm">Done</span>
          )}
        </div>
        {status !== 'pending' && (
          <p className="text-sm text-gray-500 mt-1">{step.description}</p>
        )}
      </div>
    </div>
  );
};

// Main Runner Component
const AgentRunner = ({ agent, API, onComplete, onCancel }) => {
  const [running, setRunning] = useState(false);
  const [currentStep, setCurrentStep] = useState(-1);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [showConfetti, setShowConfetti] = useState(false);
  const abortControllerRef = useRef(null);
  const timerRef = useRef(null);

  // Get agent-specific config
  const agentType = agent?.agent_type || 'content_engine';
  const config = AGENT_CONFIGS[agentType] || AGENT_CONFIGS.content_engine;
  const STEPS = config.steps;
  
  const progress = running ? Math.min(((currentStep + 1) / STEPS.length) * 100, 99) : (result ? 100 : 0);
  const estimatedRemaining = Math.max(0, config.estimatedTime - elapsedTime);

  // Timer effect
  useEffect(() => {
    if (running) {
      timerRef.current = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);
    } else {
      if (timerRef.current) clearInterval(timerRef.current);
    }
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [running]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const runAgent = async () => {
    setRunning(true);
    setError(null);
    setCurrentStep(0);
    setElapsedTime(0);
    
    abortControllerRef.current = new AbortController();

    // Simulate step progress while waiting
    const stepDuration = (config.estimatedTime * 1000) / STEPS.length;
    const progressInterval = setInterval(() => {
      setCurrentStep(prev => {
        if (prev < STEPS.length - 2) return prev + 1;
        return prev;
      });
    }, stepDuration);

    try {
      const token = localStorage.getItem('token');
      
      // Use quick-run endpoint if no agent ID, otherwise use specific agent run
      const endpoint = agent.id 
        ? `${API}/agents/${agent.id}/run`
        : `${API}/agents/quick-run/${agentType}`;
      
      // Build config body for the request
      const requestBody = {};
      if (agent.spy_config) {
        requestBody.spy_config = agent.spy_config;
      }
      if (agent.content_preferences) {
        requestBody.content_preferences = agent.content_preferences;
      }
      
      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody),
        signal: abortControllerRef.current.signal
      });

      if (!response.ok) {
        clearInterval(progressInterval);
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || `Server error: ${response.status}`);
      }
      
      const data = await response.json();

      // If backend returned a job_id, poll for completion instead of waiting
      if (data.job_id) {
        clearInterval(progressInterval);
        const finalData = await pollJobUntilDone(data.job_id, token);
        setCurrentStep(STEPS.length);
        setResult(finalData);
        setShowConfetti(true);
        setTimeout(() => setShowConfetti(false), 3000);
        return;
      }
      
      clearInterval(progressInterval);

      if (!data.success) {
        throw new Error(data.message || data.detail || 'Agent execution failed');
      }

      setCurrentStep(STEPS.length);
      setResult(data);
      setShowConfetti(true);
      setTimeout(() => setShowConfetti(false), 3000);

    } catch (err) {
      clearInterval(progressInterval);
      if (err.name !== 'AbortError') {
        console.error('Agent run error:', err);
        setError(err.message || 'An unexpected error occurred');
      }
    } finally {
      setRunning(false);
    }
  };

  const pollJobUntilDone = (jobId, token) => {
    return new Promise((resolve, reject) => {
      const poll = setInterval(async () => {
        try {
          const res = await fetch(`${API}/agents/jobs/${jobId}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          if (!res.ok) return; // transient error, keep polling
          const job = await res.json();
          if (job.status === 'completed') {
            clearInterval(poll);
            resolve({ success: true, report: job.result, job_id: jobId });
          } else if (job.status === 'failed') {
            clearInterval(poll);
            reject(new Error(job.error || 'Agent job failed'));
          }
          // Still running — keep polling
        } catch (e) {
          // Network error, keep polling
        }
      }, 3000);
    });
  };

  const handleAbort = () => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    setRunning(false);
    setCurrentStep(-1);
  };

  // Success View
  if (result) {
    return (
      <div className="relative bg-[#0a0a0f] rounded-3xl overflow-hidden">
        <Confetti show={showConfetti} />
        
        {/* Success Header */}
        <div className={`relative p-8 text-center bg-gradient-to-b ${config.gradientBg}`}>
          <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/5 to-transparent animate-shimmer" />
          
          <div className="relative">
            <div className="text-7xl mb-4 animate-bounce">{config.successEmoji}</div>
            <h2 className="text-3xl font-bold text-white mb-2">{config.successTitle}</h2>
            <p className="text-gray-400">
              {config.name} completed in {formatTime(elapsedTime)} • Used ~{config.credits} credits
            </p>
          </div>
        </div>

        {/* Results Summary */}
        <div className="p-8">
          {/* Content Engine Results */}
          {agentType === 'content_engine' && result.package && (
            <>
              <div className="bg-gray-900/50 rounded-2xl p-6 mb-6 border border-gray-800">
                <h3 className="font-semibold text-white mb-2 flex items-center gap-2">
                  <span>📝</span> {result.package?.topic}
                </h3>
                <p className="text-gray-400 text-sm">{result.package?.topic_rationale}</p>
              </div>

              <div className="grid grid-cols-4 gap-4 mb-6">
                {[
                  { label: 'Words', value: result.package?.blog_post?.word_count || 0, color: 'purple' },
                  { label: 'Platforms', value: Object.keys(result.package?.social_media || {}).length, color: 'blue' },
                  { label: 'SEO Score', value: result.package?.blog_post?.seo_score || 0, color: 'emerald' },
                  { label: 'Images', value: result.package?.featured_image ? 1 : 0, color: 'amber' }
                ].map((stat, i) => (
                  <div key={i} className="text-center p-4 bg-gray-900/50 rounded-xl border border-gray-800">
                    <div className={`text-3xl font-bold text-${stat.color}-400`}>{stat.value}</div>
                    <div className="text-sm text-gray-500">{stat.label}</div>
                  </div>
                ))}
              </div>
            </>
          )}

          {/* Competitor Spy Results */}
          {agentType === 'competitor_spy' && result.report && (
            <>
              <div className="bg-amber-500/10 rounded-2xl p-6 mb-6 border border-amber-500/20">
                <h3 className="font-semibold text-amber-400 mb-2">🎯 Intelligence Summary</h3>
                <p className="text-gray-300">
                  Analyzed {result.report?.competitors_analyzed || 0} competitors and identified {result.report?.content_gaps?.gaps?.length || result.report?.content_gaps?.length || 0} content opportunities
                </p>
              </div>

              <div className="grid grid-cols-3 gap-4 mb-6">
                <div className="text-center p-4 bg-gray-900/50 rounded-xl border border-gray-800">
                  <div className="text-3xl font-bold text-amber-400">{result.report?.competitors_analyzed || 0}</div>
                  <div className="text-sm text-gray-500">Competitors</div>
                </div>
                <div className="text-center p-4 bg-gray-900/50 rounded-xl border border-gray-800">
                  <div className="text-3xl font-bold text-orange-400">
                    {result.report?.content_gaps?.gaps?.length || 
                     (Array.isArray(result.report?.content_gaps) ? result.report.content_gaps.length : 0) || 0}
                  </div>
                  <div className="text-sm text-gray-500">Gaps Found</div>
                </div>
                <div className="text-center p-4 bg-gray-900/50 rounded-xl border border-gray-800">
                  <div className="text-3xl font-bold text-emerald-400">
                    {result.report?.opportunities?.ranked_opportunities?.length || 
                     result.report?.opportunities?.top_opportunities?.length || 
                     (Array.isArray(result.report?.opportunities) ? result.report.opportunities.length : 0) || 0}
                  </div>
                  <div className="text-sm text-gray-500">Opportunities</div>
                </div>
              </div>

              {result.report?.top_gap && (
                <div className="bg-gradient-to-r from-amber-500/10 to-orange-500/10 rounded-xl p-4 mb-6 border border-amber-500/20">
                  <p className="text-xs text-amber-500 font-semibold mb-1">🎯 TOP OPPORTUNITY</p>
                  <p className="text-white font-medium">
                    {typeof result.report.top_gap === 'string' 
                      ? result.report.top_gap 
                      : (result.report.top_gap?.title || result.report.top_gap?.topic || result.report.top_gap?.reason || 'Strategic opportunity identified')}
                  </p>
                </div>
              )}
            </>
          )}

          {/* Action Button */}
          <button
            onClick={() => onComplete(result)}
            className={`w-full py-4 rounded-xl font-bold text-white bg-gradient-to-r ${config.gradient} hover:shadow-lg ${config.glow} transition-all duration-300`}
          >
            View Full {agentType === 'competitor_spy' ? 'Report' : 'Package'} →
          </button>
        </div>
        
        <style>{`
          @keyframes shimmer {
            0% { transform: translateX(-100%); }
            100% { transform: translateX(100%); }
          }
          .animate-shimmer { animation: shimmer 2s infinite; }
        `}</style>
      </div>
    );
  }

  // Running / Ready View
  return (
    <div className="bg-[#0a0a0f] rounded-3xl overflow-hidden border border-gray-800">
      {/* Header */}
      <div className={`relative p-6 bg-gradient-to-b ${config.gradientBg}`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-3xl shadow-lg ${config.glow}`}>
              {running ? config.runningEmoji : config.emoji}
            </div>
            <div>
              <h2 className="text-xl font-bold text-white">
                {running ? `${config.name} is Working...` : `Run ${agent?.name || config.name}`}
              </h2>
              <p className="text-gray-400">
                {running ? config.description : `~${config.credits} credits • ~${Math.ceil(config.estimatedTime / 60)} min`}
              </p>
            </div>
          </div>
          
          {/* Progress Ring */}
          {running && (
            <div className="relative">
              <ProgressRing progress={progress} />
              <div className="absolute inset-0 flex items-center justify-center">
                <span className="text-2xl font-bold text-white">{Math.round(progress)}%</span>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Stats Bar */}
      {running && (
        <div className="px-6 py-4 bg-gray-900/50 border-y border-gray-800 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <div>
              <p className="text-xs text-gray-500">Elapsed</p>
              <p className="text-lg font-mono text-white">{formatTime(elapsedTime)}</p>
            </div>
            <div className="h-8 w-px bg-gray-700" />
            <div>
              <p className="text-xs text-gray-500">Est. Remaining</p>
              <p className="text-lg font-mono text-gray-400">{formatTime(estimatedRemaining)}</p>
            </div>
            <div className="h-8 w-px bg-gray-700" />
            <div>
              <p className="text-xs text-gray-500">Credits</p>
              <p className="text-lg font-semibold text-amber-400">~{config.credits}</p>
            </div>
          </div>
          
          <button
            onClick={handleAbort}
            className="px-4 py-2 text-red-400 hover:bg-red-500/10 rounded-lg transition-colors text-sm"
          >
            ✕ Cancel
          </button>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mx-6 mt-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl">
          <div className="flex items-start gap-3">
            <span className="text-red-400 text-xl">⚠️</span>
            <div>
              <p className="font-semibold text-red-400">Error</p>
              <p className="text-gray-400 text-sm">{error}</p>
            </div>
          </div>
        </div>
      )}

      {/* Steps Progress */}
      <div className="p-6">
        <div className="space-y-0">
          {STEPS.map((step, idx) => {
            let status = 'pending';
            if (idx < currentStep) status = 'complete';
            else if (idx === currentStep && running) status = 'active';
            
            return (
              <StepItem
                key={idx}
                step={step}
                index={idx}
                status={status}
                isLast={idx === STEPS.length - 1}
              />
            );
          })}
        </div>
      </div>

      {/* Actions */}
      {!running && (
        <div className="p-6 pt-0 flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 py-4 rounded-xl border border-gray-700 text-gray-400 hover:border-gray-600 hover:text-gray-300 transition-all duration-300 font-medium"
          >
            Cancel
          </button>
          <button
            onClick={runAgent}
            className={`flex-1 py-4 rounded-xl font-bold text-white bg-gradient-to-r ${config.gradient} hover:shadow-lg ${config.glow} transition-all duration-300`}
          >
            {config.emoji} Start {config.name}
          </button>
        </div>
      )}
    </div>
  );
};

export default AgentRunner;
